import React, { memo, useCallback } from 'react';
import { Form, Spin } from 'antd';
import useDebouncedSearch from '../../../../../../hooks/useDebouncedSearch';
import usePeople from '../../../../queries/usePeople';
import { addUpperIndex, validationRules } from '../../constants';
import AddressField from '../components/AddressField/AddressField';
import UploadDragger from '../../../formComponents/UploadDragger';
import TextField from '../../../formComponents/TextField';
import SelectField from '../../../formComponents/SelectField';
import styles from '../../modalStyles.module.scss';

const InformationTab = ({
  numberError,
  getFieldDecorator,
  imagesList,
  setImagesList,
  handleSubmit,
  selectedAddress,
  setSelectedAddress,
  setFieldsValue,
  editingFacility,
  onNumberFieldChange,
}) => {
  const [_brokerSearch, setBrokerSearch, debouncedBrokerSearch] =
    useDebouncedSearch();
  const [_managerSearch, setManagerSearch, debouncedManagerSearch] =
    useDebouncedSearch();

  const { data: brokers, isFetching: brokersLoading } = usePeople(
    debouncedBrokerSearch
  );
  const { data: managers, isFetching: managersLoading } = usePeople(
    debouncedManagerSearch
  );

  const onBrokerSearch = useCallback(
    (search) => setBrokerSearch(search),
    [setBrokerSearch]
  );
  const onManagerSearch = useCallback(
    (search) => setManagerSearch(search),
    [setManagerSearch]
  );

  return (
    <Form layout="vertical" onSubmit={handleSubmit}>
      <AddressField
        editingFacility={editingFacility}
        selectedAddress={selectedAddress}
        setSelectedAddress={setSelectedAddress}
        getFieldDecorator={getFieldDecorator}
        setFieldsValue={setFieldsValue}
      />
      <UploadDragger
        label="Изображения объекта"
        fileList={imagesList}
        onlyPicture
        setState={setImagesList}
        iconProps={{ type: 'picture' }}
        description="перетащите , или нажмите, чтобы загрузить
            изображение"
      />

      <div className={styles.flex}>
        <TextField
          label="Номер объекта"
          className={styles.flexHalf}
          validateStatus={numberError ? 'error' : undefined}
          help={numberError ? 'Номер объекта уже существует.' : undefined}
          name="number"
          getFieldDecorator={getFieldDecorator}
          options={{
            initialValue: editingFacility?.number,
            rules: [validationRules.required('Укажите номер объекта')],
          }}
          inputProps={{ onChange: onNumberFieldChange }}
        />

        <TextField
          label="Название объекта"
          className={styles.flexHalf}
          name="name"
          getFieldDecorator={getFieldDecorator}
          options={{
            initialValue: editingFacility?.name,
            rules: [validationRules.required('Укажите название объекта')],
          }}
        />
      </div>

      <div className={styles.flex}>
        <SelectField
          className={styles.flexFull}
          label="Брокер"
          name="broker"
          getFieldDecorator={getFieldDecorator}
          items={brokers}
          selectProps={{
            notFoundContent: brokersLoading ? <Spin size="small" /> : undefined,
            onSearch: onBrokerSearch,
            showSearch: true,
            placeholder: 'Выберите из списка',
            loading: brokersLoading,
            filterOption: false,
          }}
          options={{
            initialValue: editingFacility?.broker?.id,
            rules: [validationRules.required('Выберите брокера', 'number')],
          }}
          getContent={(item) => item.fullName.trim()}
        />

        <SelectField
          className={styles.flexFull}
          label="Управляющий"
          name="manager"
          getFieldDecorator={getFieldDecorator}
          items={managers}
          selectProps={{
            notFoundContent: managersLoading ? (
              <Spin size="small" />
            ) : undefined,
            onSearch: onManagerSearch,
            showSearch: true,
            placeholder: 'Выберите из списка',
            loading: managersLoading,
            filterOption: false,
          }}
          options={{
            initialValue: editingFacility?.manager?.id,
            rules: [
              validationRules.required('Выберите управляющего', 'number'),
            ],
          }}
          getContent={(item) => item.fullName.trim()}
        />
      </div>

      <div className={styles.flex}>
        <TextField
          className={styles.flexFull}
          label="Общая площадь"
          name="totalArea"
          options={{
            initialValue:
              editingFacility?.totalArea && String(editingFacility.totalArea),
            rules: [
              validationRules.required('Укажите площадь объекта'),
              validationRules.number,
            ],
          }}
          getFieldDecorator={getFieldDecorator}
          inputProps={{ suffix: addUpperIndex('м', '2') }}
        />

        <TextField
          className={styles.flexFull}
          label="Количество помещений"
          name="officesAmount"
          options={{
            initialValue:
              editingFacility?.officesAmount &&
              String(editingFacility.officesAmount),
            rules: [
              validationRules.required('Укажите количество помещений'),
              validationRules.integer,
            ],
          }}
          getFieldDecorator={getFieldDecorator}
        />

        <TextField
          className={styles.flexFull}
          label="Площадь МОП"
          name="mopArea"
          options={{
            initialValue:
              editingFacility?.mopArea && String(editingFacility.mopArea),
            rules: [validationRules.number],
          }}
          getFieldDecorator={getFieldDecorator}
          inputProps={{ suffix: addUpperIndex('м', '2') }}
        />
      </div>

      <TextField
        label="Кадастровый номер"
        name="cadastralNumber"
        options={{
          initialValue: editingFacility?.cadastralNumber,
        }}
        getFieldDecorator={getFieldDecorator}
      />

      <TextField
        isTextArea
        label="Описание"
        name="description"
        options={{
          initialValue: editingFacility?.description?.description,
        }}
        getFieldDecorator={getFieldDecorator}
        inputProps={{ rows: 3, placeholder: 'Описание объекта' }}
      />

      <TextField
        isTextArea
        label="Комментарий"
        name="comment"
        options={{
          initialValue: editingFacility?.comment,
        }}
        getFieldDecorator={getFieldDecorator}
        inputProps={{ rows: 3, placeholder: 'Комментарий' }}
      />
    </Form>
  );
};

export default memo(InformationTab);
